import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getFrameImageUrl, getShotVersions, restoreShotVersion, getSceneContext, getConceptsForShot, getConceptImageUrl } from "@/services/shotService";
import { toast } from "sonner";
import { RotateCcw, Sparkles, User, ArrowUpCircle, FileText, ChevronRight, ChevronLeft } from "lucide-react";

/**
 * Extract dominant color from an image for ambient lighting.
 */
function useDominantColor(imageUrl) {
  const [color, setColor] = useState("rgba(0,0,0,0)");
  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.crossOrigin = null;
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 8;
        canvas.height = 8;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 8, 8);
        const data = ctx.getImageData(0, 0, 8, 8).data;
        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i]; g += data[i + 1]; b += data[i + 2]; count++;
        }
        setColor(`rgba(${Math.round(r / count)},${Math.round(g / count)},${Math.round(b / count)},0.12)`);
      } catch {
        setColor("rgba(40,30,20,0.08)");
      }
    };
    img.src = imageUrl;
  }, [imageUrl]);
  return color;
}

function getVersionSource(ver) {
  if (ver.trigger === "style_upgrade") return { color: "#B8860B", icon: Sparkles, label: "Style Upgrade" };
  if (ver.trigger === "regenerate") return { color: "#666", icon: ArrowUpCircle, label: "Regenerate" };
  return { color: "#6b8aff", icon: User, label: ver.created_by_name || "Manual" };
}

const STAGE_COLORS = { sketch: "#8a8a7a", concept: "#5b8af5", previs: "#B8860B" };
const STAGE_LABELS = { sketch: "Sketch", concept: "Concept", previs: "Previs" };

export default function CinemaMode({ frames, projectId, onFrameClick, onUpdate, conceptLayerEnabled }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [versions, setVersions] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewLabel, setPreviewLabel] = useState(null);
  const [showUI, setShowUI] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [sceneText, setSceneText] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [activeConceptIdx, setActiveConceptIdx] = useState(-1); // -1 = show selected/default
  const hideTimerRef = useRef(null);
  const filmstripRef = useRef(null);

  const frame = frames[currentIndex];
  const imageUrl = frame?.status === "completed" ? getFrameImageUrl(frame.id) : null;
  const ambientColor = useDominantColor(imageUrl);

  // Scene groups for filmstrip
  const sceneGroups = useMemo(() => {
    const groups = [];
    let current = null;
    frames.forEach((f, i) => {
      if (!current || current.sceneNumber !== f.scene_number) {
        current = { sceneNumber: f.scene_number, startIndex: i, frames: [] };
        groups.push(current);
      }
      current.frames.push({ frame: f, index: i });
    });
    return groups;
  }, [frames]);

  // Auto-hide UI
  const resetHideTimer = useCallback(() => {
    setShowUI(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowUI(false), 4000);
  }, []);

  useEffect(() => {
    resetHideTimer();
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [currentIndex, resetHideTimer]);

  useEffect(() => {
    const handler = () => resetHideTimer();
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [resetHideTimer]);

  // Fetch versions
  useEffect(() => {
    if (!frame?.id || !projectId) return;
    let cancelled = false;
    getShotVersions(projectId, frame.id)
      .then((v) => { if (!cancelled) setVersions(v || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [frame?.id, projectId]);

  // Fetch scene context
  useEffect(() => {
    if (!frame?.scene_number || !projectId) return;
    let cancelled = false;
    getSceneContext(projectId, frame.scene_number)
      .then((ctx) => { if (!cancelled) setSceneText(ctx); })
      .catch(() => { if (!cancelled) setSceneText(null); });
    return () => { cancelled = true; };
  }, [frame?.scene_number, projectId]);

  // Fetch concepts for current frame's anchor
  useEffect(() => {
    if (!conceptLayerEnabled || !frame?.anchor_id || !projectId) {
      setConcepts([]);
      setActiveConceptIdx(-1);
      return;
    }
    let cancelled = false;
    getConceptsForShot(projectId, frame.anchor_id)
      .then((c) => { if (!cancelled) { setConcepts(c || []); setActiveConceptIdx(-1); } })
      .catch(() => { if (!cancelled) setConcepts([]); });
    return () => { cancelled = true; };
  }, [conceptLayerEnabled, frame?.anchor_id, projectId]);

  // Navigate
  const goTo = useCallback((idx) => {
    if (idx < 0 || idx >= frames.length || idx === currentIndex || transitioning) return;
    setTransitioning(true);
    setPreviewImage(null);
    setPreviewLabel(null);
    setTimeout(() => { setCurrentIndex(idx); setTransitioning(false); }, 120);
  }, [frames.length, currentIndex, transitioning]);

  const goPrev = useCallback(() => goTo(currentIndex - 1), [goTo, currentIndex]);
  const goNext = useCallback(() => goTo(currentIndex + 1), [goTo, currentIndex]);

  // Keyboard
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
      else if (e.key === "s" || e.key === "S") { setShowScript((v) => !v); }
      else if (e.key >= "1" && e.key <= "9") {
        const sn = parseInt(e.key);
        const idx = frames.findIndex((f) => f.scene_number === sn);
        if (idx >= 0) goTo(idx);
      }
      else if ((e.key === "c" || e.key === "C") && concepts.length > 1) {
        setActiveConceptIdx((prev) => (prev + 1) % concepts.length);
      }
      else if (e.key === "Escape") { setPreviewImage(null); setShowScript(false); setActiveConceptIdx(-1); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goPrev, goNext, goTo, frames]);

  // Scroll filmstrip
  useEffect(() => {
    if (filmstripRef.current) {
      const card = filmstripRef.current.querySelector(`[data-index="${currentIndex}"]`);
      if (card) card.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [currentIndex]);

  const handleRestore = async (ver) => {
    try {
      await restoreShotVersion(projectId, frame.id, ver.version_id);
      toast.success(`Restored: ${ver.name}`);
      setPreviewImage(null);
      setPreviewLabel(null);
      onUpdate?.();
    } catch {
      toast.error("Restore failed");
    }
  };

  if (!frame) return null;

  // Determine display image: preview > active concept > default frame
  const activeConceptImage = activeConceptIdx >= 0 && concepts[activeConceptIdx]
    ? getConceptImageUrl(concepts[activeConceptIdx].concept_id)
    : null;
  const displayImage = previewImage || activeConceptImage || imageUrl;
  const stage = frame.stage || "sketch";
  const stageColor = STAGE_COLORS[stage] || STAGE_COLORS.sketch;
  const hasVersions = versions.length > 0;

  return (
    <div className="relative w-full h-full overflow-hidden bg-black select-none" onMouseMove={resetHideTimer}>
      {/* Ambient glow */}
      <div className="absolute inset-0 transition-colors duration-1000"
        style={{ background: `radial-gradient(ellipse at center 40%, ${ambientColor}, transparent 70%)` }} />

      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%)" }} />

      {/* ═══════════════ MAIN LAYOUT ═══════════════ */}
      <div className="absolute inset-0 flex flex-col">
        {/* Top bar — shot metadata */}
        <motion.div
          animate={{ opacity: showUI ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          className="flex-shrink-0 flex items-center justify-between px-8 py-4 z-10"
        >
          <div>
            <div className="flex items-center gap-3">
              <span className="text-white/90 text-lg font-light tracking-wide">
                Scene {frame.scene_number}
              </span>
              <span className="text-white/20">·</span>
              <span className="text-white/60 text-lg font-light">
                Shot {frame.frame_number}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {frame.shot_type && (
                <span className="text-white/30 text-xs capitalize">{frame.shot_type.replace(/-/g, " ")}</span>
              )}
              <span className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ color: stageColor, backgroundColor: `${stageColor}12`, border: `1px solid ${stageColor}25` }}>
                {STAGE_LABELS[stage]}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Script toggle */}
            <button
              onClick={() => setShowScript((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] transition-all ${
                showScript
                  ? "bg-white/10 text-white/80 border border-white/15"
                  : "text-white/30 hover:text-white/60 border border-transparent"
              }`}
            >
              <FileText size={12} />
              Script
            </button>
            <span className="text-white/15 text-sm font-light tabular-nums">
              {currentIndex + 1} / {frames.length}
            </span>
          </div>
        </motion.div>

        {/* Center — hero image + optional script panel */}
        <div className="flex-1 flex min-h-0 px-8 pb-2 gap-4">
          {/* Nav arrow left */}
          <motion.div animate={{ opacity: showUI ? 1 : 0 }} className="flex items-center flex-shrink-0">
            {currentIndex > 0 && (
              <button onClick={goPrev} className="p-2 text-white/20 hover:text-white/60 transition-colors">
                <ChevronLeft size={28} strokeWidth={1.5} />
              </button>
            )}
          </motion.div>

          {/* Hero image */}
          <div className="flex-1 flex items-center justify-center min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${frame.id}-${previewImage || "current"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="relative max-w-full max-h-full flex flex-col items-center"
              >
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt={`Shot ${frame.frame_number}`}
                    className="max-h-[calc(100vh-280px)] max-w-full rounded-lg object-contain"

                    style={{ boxShadow: `0 16px 60px rgba(0,0,0,0.7), 0 0 80px ${ambientColor}` }}
                  />
                ) : (
                  <div className="w-[50vw] aspect-video bg-[#111] rounded-lg flex items-center justify-center">
                    <span className="text-white/15 text-sm">{frame.status === "generating" ? "Generating..." : "No image"}</span>
                  </div>
                )}

                {/* Preview label */}
                {previewLabel && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="absolute top-3 left-3 px-2.5 py-1 bg-black/70 backdrop-blur rounded text-[9px] text-white/70">
                    Previewing: {previewLabel}
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Nav arrow right */}
          <motion.div animate={{ opacity: showUI ? 1 : 0 }} className="flex items-center flex-shrink-0">
            {currentIndex < frames.length - 1 && (
              <button onClick={goNext} className="p-2 text-white/20 hover:text-white/60 transition-colors">
                <ChevronRight size={28} strokeWidth={1.5} />
              </button>
            )}
          </motion.div>

          {/* Script panel — slides in from right */}
          <AnimatePresence>
            {showScript && sceneText && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 320, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="flex-shrink-0 overflow-hidden"
              >
                <div className="w-[320px] h-full bg-white/[0.03] border border-white/5 rounded-xl p-4 overflow-y-auto scrollbar-none">
                  {/* Scene heading */}
                  <div className="mb-4">
                    <p className="text-[9px] text-[#B8860B]/60 uppercase tracking-widest mb-1">Scene {frame.scene_number}</p>
                    <p className="text-xs text-white/80 font-medium">{sceneText.heading}</p>
                    {sceneText.location && (
                      <p className="text-[10px] text-white/30 mt-0.5">{sceneText.location} · {sceneText.time_of_day}</p>
                    )}
                  </div>

                  {/* Characters */}
                  {sceneText.characters?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[8px] text-white/20 uppercase tracking-wider mb-1.5">Characters</p>
                      <div className="flex flex-wrap gap-1">
                        {sceneText.characters.map((c, i) => (
                          <span key={i} className="text-[9px] px-2 py-0.5 rounded bg-white/5 text-white/50">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Script text */}
                  {sceneText.content && (
                    <div>
                      <p className="text-[8px] text-white/20 uppercase tracking-wider mb-1.5">Script</p>
                      <div className="text-[10px] text-white/40 leading-relaxed whitespace-pre-wrap font-mono">
                        {sceneText.content.length > 1500
                          ? sceneText.content.slice(0, 1500) + "..."
                          : sceneText.content}
                      </div>
                    </div>
                  )}

                  {/* Prompt used for this frame */}
                  {frame.prompt && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <p className="text-[8px] text-white/20 uppercase tracking-wider mb-1.5">Shot Prompt</p>
                      <p className="text-[10px] text-white/30 leading-relaxed">
                        {frame.prompt.length > 500 ? frame.prompt.slice(0, 500) + "..." : frame.prompt}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ═══════════════ CONCEPT SWITCHER — when multiple concepts exist ═══════════════ */}
        {concepts.length > 1 && (
          <motion.div
            animate={{ opacity: showUI ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0 flex justify-center px-8 pb-1"
          >
            <div className="flex items-center gap-1 p-1 bg-white/[0.04] rounded-full border border-white/5">
              {concepts.map((c, idx) => {
                const isActive = idx === activeConceptIdx || (activeConceptIdx === -1 && c.is_selected);
                return (
                  <button
                    key={c.concept_id}
                    onClick={() => setActiveConceptIdx(idx)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] transition-all ${
                      isActive
                        ? "bg-white/10 text-white/80 border border-white/15"
                        : "text-white/30 hover:text-white/60 border border-transparent"
                    }`}
                  >
                    {c.creator_picture ? (
                      <img src={c.creator_picture} alt="" className="w-4 h-4 rounded-full object-cover" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-white/10 flex items-center justify-center">
                        <User size={8} className="text-white/40" />
                      </div>
                    )}
                    <span className="max-w-[80px] truncate">{c.creator_name || "Unknown"}</span>
                    {c.is_selected && (
                      <span className="text-[#B8860B] text-[8px]">★</span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ═══════════════ VERSION STRIP — always visible if versions exist ═══════════════ */}
        {hasVersions && (
          <div className="flex-shrink-0 px-8 pb-1">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-none py-1">
              {/* "Current" label */}
              <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                <div
                  className={`w-20 aspect-video rounded-md overflow-hidden cursor-pointer border-2 transition-all ${
                    !previewImage ? "border-white/30" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  onMouseEnter={() => { setPreviewImage(null); setPreviewLabel(null); }}
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt="Current" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#1a1a1a]" />
                  )}
                </div>
                <span className="text-[7px] text-white/30">Current</span>
              </div>

              {/* Divider */}
              <div className="flex-shrink-0 w-px h-8 bg-white/10" />

              {/* Version thumbnails */}
              {versions.map((ver) => {
                const source = getVersionSource(ver);
                const verImg = ver.image_data ? `data:image/png;base64,${ver.image_data}` : null;
                const isPreview = previewImage === verImg;

                return (
                  <div key={ver.version_id} className="flex-shrink-0 flex flex-col items-center gap-0.5">
                    <motion.div
                      whileHover={{ y: -3, scale: 1.05 }}
                      className={`w-20 aspect-video rounded-md overflow-hidden cursor-pointer border-2 transition-all relative group ${
                        isPreview ? "border-white/30" : "border-transparent opacity-50 hover:opacity-100"
                      }`}
                      style={{ borderColor: isPreview ? source.color : undefined }}
                      onMouseEnter={() => { if (verImg) { setPreviewImage(verImg); setPreviewLabel(ver.name); } }}
                      onMouseLeave={() => { setPreviewImage(null); setPreviewLabel(null); }}
                      onClick={() => handleRestore(ver)}
                    >
                      {verImg ? (
                        <img src={verImg} alt={ver.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-[#0d0d0d]" />
                      )}
                      {/* Source color top strip */}
                      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ backgroundColor: source.color }} />
                      {/* Restore on hover */}
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <RotateCcw size={10} className="text-white/80" />
                      </div>
                    </motion.div>
                    <div className="flex items-center gap-0.5">
                      <source.icon size={6} style={{ color: source.color }} />
                      <span className="text-[6px] text-white/25 max-w-[72px] truncate">{ver.created_by_name || ver.name}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══════════════ FILMSTRIP ═══════════════ */}
        <div className="flex-shrink-0 bg-black/50 backdrop-blur-lg border-t border-white/5 px-4 py-2.5">
          <div ref={filmstripRef} className="flex gap-1 overflow-x-auto scrollbar-none items-center">
            {sceneGroups.map((group) => (
              <div key={group.sceneNumber} className="flex items-center gap-1 flex-shrink-0">
                <div className="flex flex-col items-center px-1.5 flex-shrink-0">
                  <span className="text-[6px] text-[#B8860B]/50 uppercase tracking-widest whitespace-nowrap">S{group.sceneNumber}</span>
                </div>
                {group.frames.map(({ frame: f, index: idx }) => {
                  const isActive = idx === currentIndex;
                  const fImgUrl = f.status === "completed" ? getFrameImageUrl(f.id) : null;
                  return (
                    <motion.button
                      key={f.id}
                      data-index={idx}
                      onClick={() => goTo(idx)}
                      whileHover={{ y: -3 }}
                      className={`flex-shrink-0 rounded overflow-hidden transition-all duration-200 ${
                        isActive ? "ring-1 ring-white/40" : "opacity-30 hover:opacity-70"
                      }`}
                      style={{ width: isActive ? 64 : 48, aspectRatio: "16/9" }}
                    >
                      {fImgUrl ? (
                        <img src={fImgUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-[#1a1a1a]" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Keyboard hints */}
      <motion.div animate={{ opacity: showUI ? 0.25 : 0 }} transition={{ duration: 0.4 }}
        className="absolute bottom-[52px] right-4 z-10 text-[7px] text-white/15 text-right pointer-events-none">
        <p>← → Navigate · S Script · {concepts.length > 1 ? 'C Concepts · ' : ''}1-9 Scene</p>
      </motion.div>
    </div>
  );
}
